import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatEUR, formatDate, API, apiErr, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  MoreVertical,
  Receipt,
  Send,
  Trash2,
  Copy,
  Download,
  CheckCircle,
  Building2,
  FileWarning,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  emitida: "bg-blue-100 text-blue-700 border border-blue-200",
  pagada: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  vencida: "bg-rose-100 text-rose-700 border border-rose-200",
  borrador: "bg-slate-100 text-slate-700 border border-slate-200",
  anulada: "bg-slate-100 text-slate-500 border border-slate-200",
};

export default function FacturasPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todas");
  const [payDlg, setPayDlg] = useState(null);
  const [payForm, setPayForm] = useState({ paid_at: todayISO(), payment_method: "transferencia" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((i) => filter === "todas" ? true : i.status === filter);

  const downloadPdf = async (id, number) => {
    const token = localStorage.getItem("chronovia_token");
    const r = await fetch(`${API}/invoices/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadXml = async (id, number) => {
    const token = localStorage.getItem("chronovia_token");
    const r = await fetch(`${API}/invoices/${id}/xml`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${number}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sendClient = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/send-client`);
      if (data.sent) toast.success("Enviada al cliente");
      else toast.error("No se pudo enviar (revisa el email del cliente)");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const sendGestoria = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/send-gestoria`);
      if (data.sent) toast.success("Enviada a la gestoría");
      else toast.error("No se pudo enviar");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/duplicate`);
      toast.success(`Duplicada como ${data.number}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const rectify = async (id) => {
    if (!window.confirm("¿Emitir factura rectificativa (importes negativos)?")) return;
    try {
      const { data } = await api.post(`/invoices/${id}/rectify`);
      toast.success(`Rectificativa ${data.number}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta factura?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Eliminada");
    load();
  };

  const openPay = (inv) => {
    setPayDlg(inv);
    setPayForm({ paid_at: todayISO(), payment_method: "transferencia" });
  };

  const confirmPay = async () => {
    try {
      await api.post(`/invoices/${payDlg.id}/mark-paid`, payForm);
      toast.success("Cobro registrado");
      setPayDlg(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="facturas-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Facturas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Numeración correlativa automática, PDF y XML Facturae.
          </p>
        </div>
        <Link to="/app/facturas/nueva">
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Nueva factura
          </Button>
        </Link>
      </div>

      <Card className="p-5">
        <Tabs value={filter} onValueChange={setFilter} className="mb-4">
          <TabsList data-testid="invoice-filter-tabs">
            <TabsTrigger value="todas">Todas</TabsTrigger>
            <TabsTrigger value="emitida">Pendientes</TabsTrigger>
            <TabsTrigger value="pagada">Pagadas</TabsTrigger>
            <TabsTrigger value="vencida">Vencidas</TabsTrigger>
            <TabsTrigger value="borrador">Borradores</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="text-center py-10 text-slate-500">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <Receipt className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">
              No hay facturas {filter !== "todas" ? `en estado "${filter}"` : ""}.
            </p>
            <Link to="/app/facturas/nueva">
              <Button
                className="mt-4 bg-blue-600 hover:bg-blue-700"
                data-testid="empty-new-invoice"
              >
                Crear primera factura
              </Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Número</th>
                  <th className="py-2 pr-4">Cliente</th>
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Vencimiento</th>
                  <th className="py-2 pr-4">Estado</th>
                  <th className="py-2 pr-4 text-right">Total</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`invoice-row-${inv.id}`}
                  >
                    <td className="py-3 pr-4 font-medium text-slate-900">
                      {inv.number}
                      {inv.is_rectificativa && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          Rect.
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-slate-700">
                      {inv.client_snapshot?.name}
                    </td>
                    <td className="py-3 pr-4 text-slate-600">
                      {formatDate(inv.issue_date)}
                    </td>
                    <td className="py-3 pr-4 text-slate-600">
                      {inv.due_date ? formatDate(inv.due_date) : "-"}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded ${
                          STATUS_MAP[inv.status] || STATUS_MAP.borrador
                        }`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right font-medium text-slate-900">
                      {formatEUR(inv.total)}
                    </td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`invoice-actions-${inv.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => downloadPdf(inv.id, inv.number)}>
                            <Download className="w-4 h-4 mr-2" /> Descargar PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadXml(inv.id, inv.number)}>
                            <Download className="w-4 h-4 mr-2" /> Descargar XML Facturae
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => sendClient(inv.id)}>
                            <Send className="w-4 h-4 mr-2" /> Enviar al cliente
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => sendGestoria(inv.id)}>
                            <Building2 className="w-4 h-4 mr-2" /> Enviar a gestoría
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {inv.status !== "pagada" && (
                            <DropdownMenuItem onClick={() => openPay(inv)}>
                              <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />{" "}
                              Marcar como pagada
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => duplicate(inv.id)}>
                            <Copy className="w-4 h-4 mr-2" /> Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => rectify(inv.id)}>
                            <FileWarning className="w-4 h-4 mr-2 text-amber-600" />{" "}
                            Emitir rectificativa
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => remove(inv.id)}
                            className="text-rose-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar cobro</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fecha de cobro</Label>
              <Input
                type="date"
                value={payForm.paid_at}
                onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })}
                data-testid="pay-date-input"
              />
            </div>
            <div>
              <Label>Método</Label>
              <Select
                value={payForm.payment_method}
                onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}
              >
                <SelectTrigger data-testid="pay-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="bizum">Bizum</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDlg(null)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmPay}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-pay-btn"
            >
              Registrar cobro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
